import { Loader, X } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { IFieldContext } from "../../../constants";
import { noop } from "../../../utils";
import Flex from "../../Flex";
import { BaseDropdown, IBaseDropdownProps } from "../BaseDropdown";
import Checkbox from "../Checkbox";
import { withField, IFieldProps } from "../Field";
import FitInput from "../FitInput/FitInput";
import * as S from "./styled";
import { IMultiselectOption } from "./types";

export interface IMultiSelectProps<
    V extends string | number | boolean,
    L extends string,
    T extends string,
    G extends string
> extends IFieldContext,
        Partial<IBaseDropdownProps> {
    value?: IMultiselectOption<V, L>[];
    controlled?: boolean;
    name?: T;
    options: IMultiselectOption<V, L>[];
    optionGroups: G[];
    onChange: (value: IMultiselectOption<V, L>[]) => void;
    onSearch?: (newSearch: string) => void;
    pattern?: RegExp;
    disabled?: boolean;
    disableClear?: boolean;
    loading?: boolean;
    trackName?: string;
    trackData?: object;
    placeholder?: string;
    children?: (option: IMultiselectOption<V, L>, place: "dropdown" | "input") => JSX.Element;
    noResults?: React.ReactNode;
    filterable?: boolean;
    withBubble?: boolean;
    bubbleLabel?: string;
}

const renderOptionDefault = (option: IMultiselectOption) => {
    return <span>{option.label}</span>;
};

const MultiSelect = <V extends string | number | boolean, L extends string, T extends string, G extends string>({
    value = [],
    controlled,
    name,
    options,
    optionGroups,
    onChange,
    onSearch,
    pattern,
    disabled = false,
    disableClear = false,
    loading = false,
    placeholder,
    children = renderOptionDefault,
    noResults,
    filterable = true,
    withBubble = false,
    bubbleLabel,
    ...props
}: IMultiSelectProps<V, L, T, G>): React.JSX.Element => {
    const [selectedOptions, setInternalSelectedOptions] = React.useState<typeof options>(value);
    const [search, setSearch] = React.useState<string>("");
    const inputRef = React.useRef<HTMLInputElement>(null);
    const filteredList = useMemo(() => {
        return search !== ""
            ? options.filter(option => option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
            : options;
    }, [search, options]);

    // The reasons for this: ensures that only unique items are selected
    // + improves performance for "includes" operations performed below
    const selectedValues = useMemo(() => {
        return new Set<V>(selectedOptions.map(o => o.value));
    }, [selectedOptions]);

    const setSelectedOptions = useCallback(
        (newOptions: typeof options) => {
            if (controlled) {
                onChange(newOptions);
            } else {
                setInternalSelectedOptions(newOptions);
            }
        },
        [controlled]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (pattern && pattern.test(e.target.value)) {
            setSearch(e.target.value);
            onSearch && onSearch(e.target.value);
            return;
        }

        if (!pattern) {
            setSearch(e.target.value);
            onSearch && onSearch(e.target.value);
        }
    };

    const handleSelectOption = (newValue: IMultiselectOption<V, L>) => {
        const foundType: IMultiselectOption<V, L> | undefined = options.find(
            option => option.value === newValue?.value
        );

        if (foundType) {
            if (selectedValues.has(foundType.value)) {
                const newOptions = (() => {
                    const result = [...selectedOptions];
                    const index = result.findIndex(val => val.value === foundType.value);
                    if (index > -1) {
                        result.splice(index, 1);
                    }
                    return result;
                })();
                setSelectedOptions(newOptions);
            } else {
                setSelectedOptions([...selectedOptions, foundType]);
            }
        }
        setSearch("");
        onSearch && onSearch("");
    };

    const handleClear = (e: React.MouseEvent<HTMLDivElement>, v: IMultiselectOption<V, L>) => {
        e.preventDefault();
        e.stopPropagation();

        handleSelectOption(v);
        setSearch("");
        onSearch && onSearch("");
    };

    React.useEffect(() => {
        if (!controlled) {
            onChange(selectedOptions);
        }
    }, [selectedOptions]);

    React.useEffect(() => {
        if (controlled) {
            setInternalSelectedOptions(value || []);
        }
    }, [value]);

    const groupedFilteredList = React.useMemo<{ [key: string]: IMultiselectOption<V, L>[] }>(() => {
        return filteredList.reduce((acc, cur) => {
            const id = cur.group;
            const group = acc[id] || [];
            group.push(cur);

            return {
                ...acc,
                [id]: group
            };
        }, {});
    }, [filteredList]);

    return (
        <BaseDropdown
            disabled={disabled}
            onDropdownOpen={() => {
                inputRef.current?.focus();
            }}
            onDropdownClose={() => {
                setSearch("");
                onSearch && onSearch("");
            }}
            {...props}
            dropdownContent={
                <>
                    {optionGroups.map(group => (
                        <React.Fragment key={group}>
                            {groupedFilteredList[group] ? (
                                <>
                                    {optionGroups.length > 1 && S.OptionsGroup && (
                                        <S.OptionsGroup>{group}</S.OptionsGroup>
                                    )}
                                    {groupedFilteredList[group].map(option => (
                                        <S.DropdownItem
                                            key={`${option.value}_${option.label}`}
                                            isActive={selectedValues.has(option.value)}
                                            onClick={() => {
                                                handleSelectOption(option);
                                                inputRef.current?.focus();
                                            }}
                                        >
                                            <Flex gap={8} alignItems="center" style={{ pointerEvents: "none" }}>
                                                <Checkbox
                                                    trackName="Multiselect.Checkbox"
                                                    onChange={noop}
                                                    checked={selectedValues.has(option.value)}
                                                />
                                                {children(option, "dropdown")}
                                            </Flex>
                                        </S.DropdownItem>
                                    ))}
                                </>
                            ) : null}
                        </React.Fragment>
                    ))}
                    {filteredList.length === 0 && <S.DropdownItem>{noResults}</S.DropdownItem>}
                </>
            }
        >
            <S.SelectedOptionWrapper>
                {!withBubble &&
                    selectedOptions.map(v => (
                        <S.SelectedOption key={v.value.toString()}>
                            {children(v, "input")}
                            {!disableClear && (
                                <S.ClearIconWrapper onClick={!disabled ? e => handleClear(e, v) : undefined}>
                                    <X size={12} />
                                </S.ClearIconWrapper>
                            )}
                        </S.SelectedOption>
                    ))}
                {withBubble && selectedOptions.length > 0 && (
                    <Flex alignItems="center" gap={4}>
                        <S.SelectedOption>{selectedOptions.length}</S.SelectedOption> {bubbleLabel}
                    </Flex>
                )}
                {filterable && (
                    <FitInput
                        ref={inputRef}
                        disabled={disabled}
                        name={name}
                        onChange={handleSearch}
                        placeholder={selectedOptions.length ? undefined : placeholder}
                        value={search}
                    />
                )}
            </S.SelectedOptionWrapper>
            {loading && <Loader />}
        </BaseDropdown>
    );
};

export default withField(MultiSelect) as {
    <V extends string | number | boolean, L extends string, T extends string, G extends string>(
        props: IMultiSelectProps<V, L, T, G> & IFieldProps
    ): React.JSX.Element;
};
